import React, { useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import Modal from "@mui/material/Modal";

import {
  getBlobTree,
  getDocumentsWithChildren,
} from "../../../../pages/DocumentManagementView/DocumentGrid.utils";
import { DocumentData } from "../../../../util/interfaces/DocumentData";
import { Alerts } from "../../Alerts/Alerts";
import CreateFolderStep from "../UploadFile/CreateFolderStep";

import axios from "@/configs/axios";
import { AlertColor, CATEGORIES, MAX_METADATA_LENGTH, TYPE_FOLDER } from "@/util/constants";

interface EditModalProps {
  callbackClose: (_updatedFile: DocumentData | null) => void;
  file: DocumentData;
  documents?: DocumentData[];
  selectedPath?: { id: string; name: string }[];
}

export const EditModal: React.FC<EditModalProps> = ({
  callbackClose,
  file,
  documents,
  selectedPath,
}) => {
  const tree = getBlobTree(documents ?? []);
  const type = file.fileType === TYPE_FOLDER ? "folder" : "file";
  const [currentFile, setCurrentFile] = useState<DocumentData>(file);
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const editDocument = async () => {
    setIsLoading(true);
    const attributes: any = {};
    if (file.fileType !== TYPE_FOLDER) {
      // FileAlias is from Knowledge base, if not is Teams files
      attributes[currentFile.fileAlias || currentFile.uuid + "-" + currentFile.documentName] = {
        category: currentFile.category,
        metadata: currentFile.metadata ?? "",
        path: currentFile.path,
        uuid: currentFile.uuid,
      };
    } else {
      getDocumentsWithChildren([file.id], tree)
        .filter((file) => file.fileType !== TYPE_FOLDER)
        .forEach((f) => {
          const newPath = f.path
            .split("/")
            .filter((path) => path !== "")
            .map((path) => (path === file.documentName ? currentFile.documentName : path));
          // FileAlias is from Knowledge base, if not is Teams files
          attributes[f.fileAlias || f.uuid + "-" + f.documentName] = {
            category: f.category,
            metadata: f.metadata ?? "",
            path: newPath.join("/") + "/",
            uuid: f.uuid,
          };
        });
    }
    try {
      const url = `${process.env.REACT_APP_SERVER_HOST}/update`;
      const response = await axios.patch(
        url,
        {
          blobs: attributes,
          category_updated:
            file.fileType === TYPE_FOLDER ? false : currentFile.category !== file.category,
        },
        {
          headers: {
            "Cache-Control": "no-store",
          },
        }
      );
      if (response.status == 200) {
        callbackClose(currentFile);
      }
    } catch (e: any) {
      setAlertColor(AlertColor.Error);
      setAlertMessage(e.message || "Unexpected error while calling the reactor.");
    }
    setIsLoading(false);
  };

  return (
    <>
      <Modal open={true} onClose={() => callbackClose(null)} data-testid="modalEditFiles">
        <div
          className="modal-kb-content"
          style={{ height: "auto", width: "40vw", borderRadius: "4px" }}
          data-testid="modal_edit"
        >
          <div className="modal-kb-header">
            <button onClick={() => callbackClose(null)} className="close_icon">
              <Icon icon="bitcoin-icons:cross-filled" height={30} color="#000000" />
            </button>
            <div className="flex flow-column" style={{ gap: "15px" }}>
              <span className="modal-title" data-testid="edit-modal-title">
                Edit {type}
              </span>
              <span>Edit the details for the following {type}.</span>
            </div>
          </div>
          <div
            className="modal-kb-body flex flow-column gap-5"
            style={{ marginTop: "5px", overflow: "auto" }}
          >
            {file.fileType !== TYPE_FOLDER && (
              <>
                <div className="flex gap-5">
                  <span>
                    <b>File:</b>
                  </span>
                  <span
                    style={{
                      width: "60%",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                    title={currentFile.documentName}
                  >
                    {currentFile.documentName}
                  </span>
                </div>
                <div className="flex gap-5">
                  <span>
                    <b>File Type:</b>
                  </span>
                  <span>{currentFile.fileType}</span>
                </div>
                <div className="flex gap-5">
                  <span>
                    <b>Uploaded By:</b>
                  </span>
                  <span>{currentFile.uploadedBy}</span>
                </div>
                <div className="flex gap-5">
                  <span>
                    <b>Uploaded Date:</b>
                  </span>
                  <span>{currentFile.uploaded}</span>
                </div>
              </>
            )}

            <div className="flex flow-column" style={{ marginTop: "5px", gap: "24px" }}>
              {file.fileType !== TYPE_FOLDER && <span className="modal-title">Details</span>}
              <div className="flex space-between">
                <span>Provide the {type} details.</span>
                <span>
                  <span style={{ color: "red" }}>*</span>Indicates required
                </span>
              </div>
            </div>
            {file.fileType !== TYPE_FOLDER && (
              <div className="flex flow-column" style={{ marginTop: "10px", rowGap: "20px" }}>
                <div className="flex flow-column">
                  <span>
                    <span style={{ color: "red" }}>*</span>File category
                  </span>
                  <TextField
                    select
                    label="Select"
                    name="fileCategory"
                    value={currentFile.category}
                    onChange={(e) => {
                      setCurrentFile({
                        ...currentFile,
                        category: e.target.value,
                      });
                    }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    size="small"
                    inputProps={{ "data-testid": "categorySelector" }}
                  >
                    {CATEGORIES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="flex flow-column">
                  <span>Metadata</span>
                  <TextField
                    label="Type"
                    name="metadata"
                    value={currentFile.metadata}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_METADATA_LENGTH) {
                        setCurrentFile({
                          ...currentFile,
                          metadata: e.target.value,
                        });
                      }
                    }}
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ "data-testid": "metadataField" }}
                    margin="normal"
                    variant="outlined"
                    placeholder="Metadata improves AI search by adding context with keywords and descriptions, making search results more relevant and responses better."
                  />
                  <Typography variant="caption" display="block" gutterBottom>
                    {currentFile.metadata?.length} / {MAX_METADATA_LENGTH}
                  </Typography>
                </div>
              </div>
            )}
            {file.fileType === TYPE_FOLDER && (
              <div className="flex flow-column" style={{ marginTop: "10px", rowGap: "20px" }}>
                <CreateFolderStep
                  folderPath={currentFile.documentName}
                  onChangeCallback={(e): boolean => {
                    const value = e.target.value;
                    if (!selectedPath) {
                      return true;
                    }
                    // Lets validate if the folder exists
                    const path = [...selectedPath, { id: `constructed-${value}`, name: value }];

                    // Navigate through the flat structure using parentId relationships
                    let currentParentId: string | null = null;
                    let folderExists = false;

                    for (let i = 0; i < path.length; i++) {
                      const folderName = path[i].name.trim();

                      // Find folder with matching name and correct parent
                      const foundFolder = tree.find(
                        (doc) =>
                          doc.documentName.trim() === folderName &&
                          doc.parentId === currentParentId &&
                          doc.fileType === TYPE_FOLDER
                      );

                      if (foundFolder) {
                        currentParentId = foundFolder.id;
                        // If this is the last item in path and it's not the original folder name
                        if (i === path.length - 1 && folderName !== file.documentName.trim()) {
                          folderExists = true;
                        }
                      } else if (i < path.length - 1) {
                        // If we can't find an intermediate folder, the path is invalid
                        break;
                      }
                    }

                    setIsDisabled(!value.trim() || folderExists);
                    setCurrentFile({
                      ...currentFile,
                      documentName: value,
                    });
                    return folderExists;
                  }}
                />
              </div>
            )}
          </div>
          <div className="modal-kb-footer">
            <button
              className="save_btn"
              type="submit"
              onClick={editDocument}
              disabled={isLoading || isDisabled}
              data-testid="editButton"
            >
              {isLoading ? (
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
                  <CircularProgress size={15} />
                  <span style={{ marginLeft: 10 }}>Saving...</span>
                </div>
              ) : (
                "Save"
              )}
            </button>
            <button
              className="cancel_btn"
              onClick={() => callbackClose(null)}
              data-testid="cancelButton"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Alerts
        style={{ top: "90%", right: "unset", left: "unset" }}
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
